import React, { useMemo } from "react";
import { round } from "lodash";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { Column, Row } from "@/shared/ui/Blocks";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import { PersonScore } from "@/features/people/hooks/useDimensionsWithScores";
import { cn } from "@/shared/utils/shadcn";

export interface BucketScoreChartProps {
  score: number;
  minMidRange: number;
  maxMidRange: number;
  peopleScores?: PersonScore[];
  className?: string;
  width?: number;
}

const BUCKET_COLORS = {
  1: {
    label: "LOW",
    bg: "bg-orange-100",
    text: "text-orange-600",
    area: "bg-orange-50",
  },
  2: {
    label: "NEUTRAL",
    bg: "bg-gray-100",
    text: "text-gray-600",
    area: "bg-gray-50",
  },
  3: {
    label: "HIGH",
    bg: "bg-green-100",
    text: "text-green-500",
    area: "bg-green-50",
  },
} as const;

function PersonTooltip({
  score,
  children,
}: {
  score: PersonScore;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger className="bg-transparent rounded-full">
        {children}
      </TooltipTrigger>
      <TooltipContent className="hover:z-9990">
        <Row className="bg-black rounded-md">
          <p className="text-white">
            {score?.person?.name} <b>{round(score.value)}</b>
          </p>
        </Row>
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
}

function BucketScoreChart({
  score,
  minMidRange,
  maxMidRange,
  peopleScores,
  className,
  width = 300,
}: BucketScoreChartProps) {
  const SCORE_MIN = 0;
  const SCORE_MAX = 100;

  const bucket = useMemo(() => {
    if (!score) return 0;
    return score < minMidRange ? 1 : score > maxMidRange ? 3 : 2;
  }, [score, minMidRange, maxMidRange]);

  // Calculate positions for the three segments
  const segmentPositions = useMemo(() => {
    const lowEnd = (minMidRange / SCORE_MAX) * 100;
    const neutralStart = (minMidRange / SCORE_MAX) * 100;
    const neutralEnd = (maxMidRange / SCORE_MAX) * 100;
    const highStart = (maxMidRange / SCORE_MAX) * 100;

    return [
      {
        key: "low",
        className: "bg-orange-100",
        left: 0,
        width: lowEnd,
      },
      {
        key: "neutral",
        className: "bg-gray-100",
        left: neutralStart,
        width: neutralEnd - neutralStart,
      },
      {
        key: "high",
        className: "bg-green-100",
        left: highStart,
        width: 100 - highStart,
      },
    ];
  }, [minMidRange, maxMidRange]);

  // Calculate avatar positions for all people
  const avatarPositions = useMemo(() => {
    if (!peopleScores || peopleScores.length === 0) {
      return [];
    }

    return peopleScores.map((personScore) => {
      const personScoreValue = personScore.value || 0;
      const position = (personScoreValue / SCORE_MAX) * 100;
      return {
        position: Math.max(0, Math.min(100, position)),
        personScore,
      };
    });
  }, [peopleScores]);

  const markerStep = 10;
  const markerPositions = useMemo(() => {
    const positions: number[] = [];
    for (let i = 0; i <= 100; i += markerStep) {
      positions.push(i);
    }
    return positions;
  }, []);

  return (
    <Column className={cn("relative", className)} style={{ width }}>
      {/* Chart container */}
      <div className="relative h-6 mb-1">
        {/* Background colored areas */}
        {segmentPositions.map((segment) => (
          <div
            key={segment.key}
            className={cn(
              "absolute h-2 top-1/2 transform -translate-y-1/2",
              segment.className
            )}
            style={{
              left: `${segment.left}%`,
              width: `${segment.width}%`,
            }}
          />
        ))}

        {/* Main scale line */}
        <div className="absolute w-full h-px bg-gray-300 top-1/2 transform -translate-y-1/2" />

        {/* Tick marks */}
        <div className="absolute top-0 left-0 right-0 h-full">
          {markerPositions.map((position: number) => {
            // Determine tick color based on segment
            let tickColor = "bg-gray-400";
            const lowEnd = (minMidRange / 100) * 100;
            const highStart = (maxMidRange / 100) * 100;

            if (position < lowEnd) {
              tickColor = "bg-orange-400";
            } else if (position > highStart) {
              tickColor = "bg-green-500";
            }

            // Check if this is a boundary or end mark (should be taller)
            const isBoundary =
              position === 0 ||
              position === 100 ||
              Math.abs(position - lowEnd) < 0.5 ||
              Math.abs(position - highStart) < 0.5;

            return (
              <div
                key={position}
                className={cn("absolute w-px", tickColor)}
                style={{
                  left: `${position}%`,
                  top: isBoundary ? "20%" : "35%",
                  height: isBoundary ? "60%" : "30%",
                }}
              />
            );
          })}
        </div>

        {/* Multiple avatars positioned on the scale */}
        {avatarPositions.map((avatarData, index) => {
          const { personScore } = avatarData;
          return (
            <div
              key={index}
              className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 z-10"
              style={{
                left: `${avatarData.position}%`,
              }}
            >
              <PersonTooltip score={personScore}>
                <AvatarWithInitials
                  avatar={personScore.person?.avatar || ""}
                  initials={personScore.person?.initials || ""}
                  isContact={!!personScore.person?.is_contact}
                  name={personScore.person?.name || ""}
                  size="xs"
                  className="border border-white shadow-sm"
                />
              </PersonTooltip>
            </div>
          );
        })}
      </div>

      {/* Category labels */}
      <Row className="justify-between text-[10px] uppercase">
        {Object.entries(BUCKET_COLORS).map(([bucketValue, bucketData]) => {
          const isActive = bucket === Number(bucketValue);
          return (
            <span
              key={bucketValue}
              className={cn(
                "px-1.5 py-0.5 rounded text-[10px]",
                isActive ? "font-semibold" : "font-medium",
                bucketData.text,
                isActive && bucketData.bg
              )}
            >
              {bucketData.label}
            </span>
          );
        })}
      </Row>
    </Column>
  );
}

export default BucketScoreChart;
